// 下單連結 for Magnus and Adam — the same mechanism the v1 daily page uses
// (components/noke/dailySourcing.ts + DailySourcingLinks.tsx → POST /api/noke/merchandising/
// sourcing-links, backend noke-merchandising-sourcing.service.js, ALLOWED_HOSTS = sinsangmarket.kr).
// Nothing here fabricates a URL: a SKU with no stored link shows no link at all, never a dead one.
// The request only ever needs a SKU and the category that buckets it, so the input is structural
// rather than 訂貨-shaped: 選品's 我的清單 rows (RestockV2ListApproved) satisfy it as they are.
// Magnus 2026-09-17: 「also should have sinsang link at the list」 — the same links, not a second
// mechanism and not a second permission path.
export interface SourcingLine {
  sku: string;
  cat?: string | null;
}

// The endpoint keys its request by FIXTURE id (DEFAULT_FIXTURES in
// noke-merchandising-validation.js) and refuses anything else, so an order line is bucketed by the
// rack its category hangs on — which is also the rack the pieces will land on when they arrive.
// Mirrors DEFAULT_FIXTURES.allowed_categories; an unknown category goes to the main top rail.
const BUCKET: Record<string, string> = {
  長褲: 'pants_a', 短褲: 'pants_a',
  短袖: 'tops_left', 長袖: 'tops_left', 針織: 'tops_left', 外套: 'tops_left', 其他: 'tops_left',
  配件: 'accessories', 鞋子: 'accessories',
  MdoC: 'mdoc',
};
const FALLBACK_BUCKET = 'tops_left';

export function sourcingBucket(cat?: string | null): string {
  return BUCKET[String(cat || '').trim()] || FALLBACK_BUCKET;
}

/**
 * The 台灣倉庫 lane — MdoC. These are pieces Magnus already owns in Taiwan; they are never ordered
 * from Korea, so they need no 下單連結 and can never be 訂不到.
 *
 * WHY THE LANE AND NOT `has_link` (Magnus 2026-09-18). All 8 approved styles with no link are MdoC
 * (M01 M02 M04 M11 M16 M17 M29 M33), so `has_link === false` alone would label every one of them
 * 訂不到 — which is what the screen had been telling him for days. The lane is the honest signal and
 * `has_link` is the convenient one: the backend says the same thing in one place
 * (noke-restock-v2-rules.js:361 `isWarehouseSku`, and `sourced = lane === 'warehouse' || has_link`
 * in noke-restock-supply.js:47), so this mirrors that definition rather than inventing a second.
 * The category is the classification; the M-prefix is the same belt-and-braces the backend keeps
 * for a row that reached the browser without one, not a guess about what an M means.
 *
 * KNOWN INCOMPLETE, and NOT fixable by syncing these two copies more carefully. The backend tests
 * three arms — `M-prefix || cat === 'MdoC' || vendor === 'mdoc'` — and `vendor` is not on any row
 * shape that reaches the browser, so this function cannot evaluate it however it is written. A
 * style whose MdoC-ness lives only in `vendor` would read as a Korea line here and could be
 * labelled 訂不到 wrongly. Not live today: every path that builds these rows sets `cat: 'MdoC'`
 * too. The fix is the backend sending its own answer — `sourceOf(s).source_lane` already rides on
 * order lines (noke-restock-v2-plan.js:227) but not yet on the 選品 approved rows
 * (noke-restock-v2-list.js) — then reading `source_lane === 'warehouse'` here with the sku/cat
 * test kept only as the fallback for older payloads. Do NOT instead add a `vendor` arm to this
 * copy: there is no `vendor` to read, and a third place that decides the lane is the drift itself.
 */
export function isWarehouseLane(row?: { sku?: string | null; cat?: string | null } | null): boolean {
  if (String(row?.cat ?? '').trim() === 'MdoC') return true;
  return /^M\d+$/i.test(String(row?.sku ?? '').trim());
}

// Same guards as the endpoint, so a malformed SKU is caught here instead of 400-ing the whole page.
const SKU_OK = /^[A-Za-z0-9_-]{1,100}$/;
const MAX_SKUS = 500;
const MAX_PER_BUCKET = 300;

export interface SourcingRack {
  id: string;
  items: { sku: string; variants: never[] }[];
}

export interface SourcingRequest {
  racks: SourcingRack[];
  error: string;
}

/** Empty `variants` means "show me the product page", never a fabricated purchase quantity. */
export function buildSourcingRequest(lines: SourcingLine[]): SourcingRequest {
  const buckets = new Map<string, Set<string>>();
  const seen = new Set<string>();
  for (const line of lines || []) {
    const sku = String(line.sku ?? '').trim();
    if (!SKU_OK.test(sku)) return { racks: [], error: '商品編號格式不正確，無法查貨源連結。' };
    if (seen.has(sku)) continue;
    seen.add(sku);
    const id = sourcingBucket(line.cat);
    const skus = buckets.get(id) || new Set<string>();
    buckets.set(id, skus);
    skus.add(sku);
    if (seen.size > MAX_SKUS || skus.size > MAX_PER_BUCKET) {
      return { racks: [], error: '這次的訂貨清單超過貨源查詢上限，請先分批處理。' };
    }
  }
  const racks = Array.from(buckets, ([id, skus]) => ({
    id, items: Array.from(skus).sort().map((sku) => ({ sku, variants: [] as never[] })),
  })).filter((rack) => rack.items.length).sort((a, b) => a.id.localeCompare(b.id));
  return { racks, error: '' };
}

/** The first stored link per SKU, deduped and in the given SKU order — what 批次開啟 opens. */
export function primaryLinks(skus: string[], links: Record<string, string[]>): string[] {
  return Array.from(new Set(skus.map((sku) => links[sku]?.[0]).filter((url): url is string => !!url)));
}

export function missingLinkCount(skus: string[], links: Record<string, string[]>): number {
  return Array.from(new Set(skus)).filter((sku) => !links[sku]?.length).length;
}
